import { useRef, useMemo, useEffect } from "react";
import { toast } from "react-toastify";

import * as FRAGS from "@thatopen/fragments";
import * as THREE from "three";

import { _3drtgmLoader } from "@treegram/loaders-3drtgm";
import { _3dtgmLoader } from "@treegram/loaders-3dtgm";
import { ImgLoader } from "@treegram/loaders-img";

import useValidationContext from "@/pages/takeoff/layouts/validation/context/validation/use-validation-context.ts";
import useProjectContext from "@/services/context/project/use-project-context.ts";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";

import ENDPOINTS from "@/services/endpoints.ts";
import { ViewerLoader } from "@/hooks/viewer/use-viewer.ts";
import _3DRecord from "../models/_3d-record.ts";
import FileRecord from "@/pages/takeoff/context/project-measure/models/file-record.ts";
import KnownScaleContent from "@/pages/takeoff/context/project-organisation/models/known-scale-content.ts";
import UserDefinedContent from "@/pages/takeoff/context/project-organisation/models/user-defined-content.ts";
import FileType from "@/models/enums/file-type.ts";

const useValidationLoaders = (storeyId: string, packId: string): ViewerLoader[] => {

    const { project } = useProjectContext();
    const { projectMeasures, storeysFileData, buildingsFileData } = useProjectMeasureContext();
    const { packsData } = useValidationContext();

    const sitePositionRef = useRef<THREE.Vector3 | undefined>();

    const storeyFilesRecord = useMemo(() => storeysFileData.get(storeyId), [storeyId, storeysFileData]);

    const packFiles3D = useMemo(() => packsData.get(packId)?.files3D ?? [], [packsData, packId]);
    const globalBboxRef = useRef<THREE.Box3>(new THREE.Box3());

    const files3drtgm = useMemo(
        () => packFiles3D.filter((f) => f.filePath.endsWith(".3drtgm")),
        [packFiles3D]
    );

    const files3dtgm = useMemo(
        () => packFiles3D.filter((f) => !f.filePath.endsWith(".3drtgm")),
        [packFiles3D]
    );

    useEffect(() => {
        const firstFile3D = Array.from(buildingsFileData.values())
            .map(files =>
                files.find(f => f.fileType === FileType.File3D && f.sitePosition != null)
            )
            .find(Boolean);

        if (firstFile3D?.sitePosition) {
            sitePositionRef.current = firstFile3D.sitePosition;
        }
    }, [buildingsFileData]);

    const planPdfLoader = useMemo<ViewerLoader<unknown, ImgLoader, FileRecord>>(() => {
        const organisationId = project.projectOrganisation.id;
        const projectId = project.id;

        return {
            key: "plan-pdf-loader",
            factory: (ctx) => new ImgLoader(ctx.viewer.getComponents(), ctx.world),
            itemsToLoad: storeyFilesRecord,
            fetchUrl: (file) => ENDPOINTS.VIEWER_PDF_PREVIEW(projectId, `${organisationId}/${projectId}/${file.relativePath}`),
            name: (file) => `${crypto.randomUUID()}_${file.name}`,
            getOptions: (file) => {
                const storey = projectMeasures.getStorey(storeyId);

                let loaderOptions = {
                    origin: new THREE.Vector2(file.origin.position.x, file.origin.position.y),
                    direction: file.origin.direction,
                    altimetry: storey?.altimetry,
                    transparent: true,
                    opacity: 1
                };

                if (file.sitePosition) {
                    loaderOptions.origin = new THREE.Vector2(
                        file.origin.position.x + file.sitePosition.x - (sitePositionRef.current?.x ?? 0),
                        file.origin.position.y + file.sitePosition.y - (sitePositionRef.current?.y ?? 0)
                    );
                    loaderOptions.altimetry = (storey?.altimetry ?? 0) - (sitePositionRef.current?.z ?? 0);
                }

                if (file.content instanceof KnownScaleContent) {
                    loaderOptions = {
                        ...loaderOptions,
                        ...{ format: file.content.format, orientation: file.content.orientation, scale: file.content.scale }
                    };
                }
                else if (file.content instanceof UserDefinedContent) {
                    loaderOptions = {
                        ...loaderOptions,
                        ...{ definedScale: file.content.scale }
                    };
                }

                return { useCoordinates: false, loaderOptions };
            },
            onItemLoaded: async (ctx, loader, file) => {
                const dimensionsCanvas = loader.getDimensionsCanvas();
                if (dimensionsCanvas) {
                    file.setDimensions(dimensionsCanvas.width, dimensionsCanvas.height);
                    const largestSide = Math.max(dimensionsCanvas.width, dimensionsCanvas.height);
                    if (largestSide > 0) {
                        await ctx.viewer.getCamera().controls.setLookAt(0, largestSide * 0.9, 0, 0, 0, 0);
                    }
                }
            },
            onError: (_ctx, _loader, file) => {
                toast.error(`Erreur lors du chargement des fichiers PDF : ${file.name}`, { autoClose: false });
            },
        };
    }, [project.id, project.projectOrganisation.id, storeyFilesRecord, projectMeasures, storeyId]);


    const boomer3drtgmLoader = useMemo<ViewerLoader<unknown, _3drtgmLoader, _3DRecord>>(
        () => ({
            key: "boomer-3drtgm-loader",
            factory: (ctx) => new _3drtgmLoader(ctx.viewer.getComponents(), ctx.world),
            itemsToLoad: files3drtgm,
            fetchUrl: (f) => ENDPOINTS.VIEWER_FILE(f.filePath),
            name: (f) => `${crypto.randomUUID()}_${f.name}`,
            options: {
                useCoordinates: false,
                loaderOptions: { lodMode: FRAGS.LodMode.ALL_GEOMETRY, loadHidden: true },
            },
            onItemLoaded: (_ctx, loader) => {
                const bbox = loader.getModel()?.box;
                if (bbox) {
                    globalBboxRef.current.union(bbox);
                }
            },
            onAllItemsLoaded: async (ctx) => {
                const bbox = globalBboxRef.current;
                if (!bbox.isEmpty()) {
                    await ctx.viewer.getCamera().controls.setLookAt(
                        bbox.max.x, bbox.max.y, bbox.max.z,
                        (bbox.max.x + bbox.min.x) / 2,
                        (bbox.max.y + bbox.min.y) / 2,
                        (bbox.max.z + bbox.min.z) / 2
                    );
                }
            },
            onError: (_ctx, _loader, f) => {
                toast.error(`Erreur lors du chargement des fichiers 3D : ${f.name}`, { autoClose: false });
            },
            onUnloaded: () => {
                globalBboxRef.current = new THREE.Box3();
            },
        }),
        [files3drtgm]
    );

    const boomer3dtgmLoader = useMemo<ViewerLoader<unknown, _3dtgmLoader, _3DRecord>>(
        () => ({
            key: "boomer-3dtgm-loader",
            factory: (ctx) => new _3dtgmLoader(ctx.viewer.getComponents(), ctx.world),
            itemsToLoad: files3dtgm,
            fetchUrl: (f) => ENDPOINTS.VIEWER_FILE(f.filePath),
            name: (f) => `${crypto.randomUUID()}_${f.name}`,
            options: {
                useCoordinates: false,
                loaderOptions: { lodMode: FRAGS.LodMode.ALL_GEOMETRY, loadHidden: true },
            },
            onItemLoaded: (_ctx, loader) => {
                const bbox = loader.getModel()?.box;
                if (bbox) {
                    globalBboxRef.current.union(bbox);
                }
            },
            onAllItemsLoaded: async (ctx) => {
                const bbox = globalBboxRef.current;
                if (!bbox.isEmpty()) {
                    await ctx.viewer.getCamera().controls.setLookAt(
                        bbox.max.x, bbox.max.y, bbox.max.z,
                        (bbox.max.x + bbox.min.x) / 2,
                        (bbox.max.y + bbox.min.y) / 2,
                        (bbox.max.z + bbox.min.z) / 2
                    );
                }
            },
            onError: (_ctx, _loader, f) => {
                toast.error(`Erreur lors du chargement des fichiers 3D : ${f.name}`, { autoClose: false });
            },
            onUnloaded: () => {
                globalBboxRef.current = new THREE.Box3();
            },
        }),
        [files3dtgm]
    );

    return useMemo<ViewerLoader[]>(
        () => [planPdfLoader, boomer3drtgmLoader, boomer3dtgmLoader],
        [planPdfLoader, boomer3drtgmLoader, boomer3dtgmLoader]
    );
}

export default useValidationLoaders;